import React, { useCallback, useEffect, useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";

import { AdminScreen, Field, Section, Toggle, UploadButton } from "@/admin/kit";
import { api, relUrl } from "@/lib/api";
import { Button } from "@/components/kit";
import { useApp } from "@/store";

const EMPTY_ART = { title: "", cover_url: "", body: "", published: false };
const sm = "!min-h-[44px]";

function TipRow({ t, i, onChanged }) {
  const { toast } = useApp();
  const [d, setD] = useState({ text: t.text, active: t.active, date: t.date ?? "" });
  const save = async () => {
    try {
      await api(`/admin/tips/${t.id}`, { method: "PUT", body: { ...d, date: d.date.trim() || null } });
      toast("Tip saved.", "success");
      onChanged();
    } catch (e) {
      toast(e.message, "error");
    }
  };
  const del = () => api(`/admin/tips/${t.id}`, { method: "DELETE" }).then(onChanged).catch((e) => toast(e.message, "error"));
  return (
    <div data-testid={`admin-tip-${i}`} className="flex flex-col gap-2 rounded-xl bg-white p-3">
      <Field testId={`admin-tip-${i}-text`} label={`Tip ${i + 1}`} value={d.text} onChange={(text) => setD({ ...d, text })} multiline />
      <Field testId={`admin-tip-${i}-date`} label="Schedule for date (YYYY-MM-DD, blank = rotate)" value={d.date} onChange={(date) => setD({ ...d, date })} />
      <Toggle testId={`admin-tip-${i}-active`} label="Active" value={d.active} onChange={(active) => setD({ ...d, active })} />
      <div className="flex gap-2">
        <Button testId={`admin-tip-${i}-save`} title="Save" variant="outline" onClick={save} className={sm} />
        <Button testId={`admin-tip-${i}-delete`} title="Delete" variant="ghost" icon={Trash2} onClick={del} className={sm} />
      </div>
    </div>
  );
}

export default function AdminContent() {
  const { toast } = useApp();
  const [tips, setTips] = useState([]);
  const [newTip, setNewTip] = useState("");
  const [arts, setArts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api("/admin/tips").then(setTips).catch((e) => toast(e.message, "error"));
    api("/admin/articles").then(setArts).catch(() => {});
  }, [toast]);
  useEffect(load, [load]);
  const addTip = async () => {
    try {
      await api("/admin/tips", { method: "POST", body: { text: newTip.trim(), active: true } });
      setNewTip("");
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };
  const saveArt = async () => {
    if (!edit) return;
    try {
      const body = { title: edit.title, cover_url: relUrl(edit.cover_url), body: edit.body, published: edit.published };
      await api(edit.id ? `/admin/articles/${edit.id}` : "/admin/articles", { method: edit.id ? "PUT" : "POST", body });
      toast(edit.published ? "Article saved & published." : "Draft saved.", "success");
      setEdit(null);
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };
  return (
    <AdminScreen title="Tips & articles" testId="admin-content">
      <div className="grid items-start gap-5 md:grid-cols-2">
        <Section title={`Daily styling tips (${tips.length})`} note="A tip scheduled for today wins; otherwise active tips rotate daily." testId="admin-tips">
          <Field testId="admin-tip-new" label="New tip" value={newTip} onChange={setNewTip} multiline />
          <Button testId="admin-tip-add" title="Add tip" icon={Plus} disabled={newTip.trim().length < 3} onClick={addTip} />
          {tips.map((t, i) => <TipRow key={t.id} t={t} i={i} onChanged={load} />)}
        </Section>
        <Section title="Articles (Learn on Home)" testId="admin-articles">
          {edit ? (
            <div className="flex flex-col gap-3">
              <Field testId="admin-article-title" label="Title" value={edit.title} onChange={(title) => setEdit({ ...edit, title })} />
              {edit.cover_url ? <img src={edit.cover_url} alt="" className="aspect-video w-full rounded-xl object-cover" /> : null}
              <UploadButton kind="image" testId="admin-article-cover" title={edit.cover_url ? "Replace cover" : "Upload cover image"} onUploaded={(u) => setEdit({ ...edit, cover_url: u.url })} onError={(m) => toast(m, "error")} />
              <Field testId="admin-article-body" label="Body" value={edit.body} onChange={(body) => setEdit({ ...edit, body })} multiline />
              <Toggle testId="admin-article-published" label="Published" value={edit.published} onChange={(published) => setEdit({ ...edit, published })} />
              <div className="flex gap-2">
                <Button testId="admin-article-save" title="Save article" icon={Check} disabled={edit.title.trim().length < 2} onClick={saveArt} className={sm} />
                <Button testId="admin-article-cancel" title="Cancel" variant="ghost" onClick={() => setEdit(null)} className={sm} />
              </div>
            </div>
          ) : (
            <Button testId="admin-article-new" title="New article" icon={Plus} onClick={() => setEdit({ ...EMPTY_ART })} />
          )}
          {arts.map((a, i) => (
            <div key={a.id} data-testid={`admin-article-${i}`} className="flex items-center gap-2 rounded-xl bg-white p-3">
              <div className="min-w-0 flex-1">
                <p className="t-strong truncate">{a.title}</p>
                <p className="t-caption">{a.published ? "Published" : "Draft"}</p>
              </div>
              <Button testId={`admin-article-${i}-edit`} title="Edit" variant="outline" onClick={() => setEdit({ id: a.id, title: a.title, cover_url: a.cover_url, body: a.body, published: a.published })} className={`${sm} !px-4`} />
              <Button testId={`admin-article-${i}-delete`} title="Delete" variant="ghost" onClick={() => api(`/admin/articles/${a.id}`, { method: "DELETE" }).then(load).catch((e) => toast(e.message, "error"))} className={`${sm} !px-3`} />
            </div>
          ))}
        </Section>
      </div>
    </AdminScreen>
  );
}
